import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import {
    DataPromise,
    DataPromiseSettings,
    getDataEmptyState,
    getDataLoadedState,
    getDataLoadingState,
    getDataRejectedState,
    shouldDispatchLoad,
    SpringBootError,
} from '../context/AxiosContext'
import useAxios from './useAxios'
import { markCanceled } from '../helpers/aiCancelFilter'

export default function useEntity<
    D, // The return type after mapping.
    R = D, // The return type from the endpoint.
    E = SpringBootError, // The error-type.
    I = string | false, // The input ID type.
    S extends DataPromiseSettings = undefined, // Additional setting type
>(
    getConfig: (id?: I) => AxiosRequestConfig,
    id: I, // Can be false to indicate we don't require any.
    mapResponse: (result: AxiosResponse<R>) => D = result => result.data as unknown as D,
    defaultData?: D,
    settings?: S,
    deps?: any[],
): [DataPromise<D, S, E>, Dispatch<SetStateAction<DataPromise<D, S, E>>>, () => void, () => Promise<void> | void] {
    const axios = useAxios()
    const [entity, setEntity] = useState(getDataLoadingState<D, S, E>(settings, defaultData))
    const [controller, setController] = useState<AbortController>()
    const resetEntity = () => setEntity(getDataEmptyState<D, S, E>(settings, defaultData))
    const loadEntity = useCallback(
        (keepLoaded = false): Promise<void> | void => {
            // Cancel any running requests.
            controller?.abort()
            if (id === undefined) {
                // Reject when we require an ID, but none has been provided.
                setEntity(getDataRejectedState<D, S, E>(settings, defaultData))
            } else if (id === null) {
                // We're not ready to load data.
                resetEntity()
            } else {
                const currentController = new AbortController()
                setEntity(previous => {
                    const loadingState = getDataLoadingState<D, S, E>(settings, previous.data)
                    // Keeping the loaded-flag lets consumers refresh in place, without a loading flash.
                    return keepLoaded ? { ...loadingState, loaded: previous.loaded } : loadingState
                })
                setController(currentController)
                return axios
                    .request<R>({
                        ...getConfig(id),
                        signal: currentController.signal,
                        headers: { SuppressErrors: 'true', ...getConfig(id).headers },
                    })
                    .then(result => {
                        return setEntity(getDataLoadedState<D, S, E>(mapResponse(result), settings))
                    })
                    .catch(error => {
                        if (error?.message === 'canceled' || error?.code === 'ERR_CANCELED') {
                            markCanceled(getConfig(id).url)
                            return resetEntity()
                        }
                        return setEntity(getDataRejectedState<D, S, E>(settings, defaultData, error))
                    })
            }
            return undefined
        },
        [id],
    )
    const reloadEntity = useCallback(() => loadEntity(true), [loadEntity])

    useEffect(
        () => {
            loadEntity()
            return () => {
                controller?.abort()
                resetEntity()
            }
        },
        deps ? [id, ...deps] : [id],
    )

    useEffect(() => {
        if (shouldDispatchLoad(entity) && id !== null) {
            loadEntity()
        }
    }, [entity])

    return [entity, setEntity, resetEntity, reloadEntity]
}
